import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { identityDocumentDisplayParts } from '../../composite-subfields.util';
import { IdentityDocumentValueComponent } from '../../../identity-document-value.component';

/**
 * Read display for an `identity-document` answer: the document type as the same neutral chip the
 * edit field uses (`.dd-doc-type-chip`, _chips.scss), followed by the masked reminder with its
 * bullets muted and the visible tail in tabular figures — `[ID-kaart] ••••1234`. The value is the
 * whole composite object `{ type, number }`; `number` is already the masked reminder, never the
 * secret.
 *
 * `identityDocumentDisplayParts` (`composite-subfields.util.ts`) is the ONE place the composition
 * rule lives — the list cell draws the same parts, and the review formatter and the list's text
 * form (sort, tooltip) print them as one string through `identityDocumentDisplayText`, so a
 * half-filled value never reads differently per surface:
 * type only → the chip alone; number only → the masked text alone; neither → '—'.
 *
 * Presentational: both inputs come from the row; the type label lives INSIDE this field's own
 * value, so there is no option-label mapping or cross-field lookup to keep here. The drawing
 * itself is `dd-identity-document-value`, shared with the list cell (#21454); this display only
 * adds the read row's em dash for an empty answer.
 */
@Component({
  selector: 'dd-identity-document-display',
  imports: [IdentityDocumentValueComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — same reason as ValueDisplayComponent: no box inside the value span.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `@if (parts().type || parts().masked) {<dd-identity-document-value [type]="parts().type" [masked]="parts().masked" />} @else {—}`,
})
export class IdentityDocumentDisplayComponent extends ReadonlyDisplayBase {
  readonly parts = computed(() => identityDocumentDisplayParts(this.value()));
}
